import { HttpResponse } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TransferOutService } from './transfer-out.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { EwayBillCreationComponent } from './eway-bill-creation/eway-bill-creation.component';
import { ToastrService } from 'ngx-toastr';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { PaginationComponent } from '../Common/pagination/pagination.component';

@Component({
  selector: 'app-transfer-out-list',
  imports: [CommonModule, FormsModule, RouterModule, NgbDropdownModule, PaginationComponent],
  templateUrl: './transfer-out-list.component.html',
  styleUrl: './transfer-out-list.component.scss',
})
export class TransferOutListComponent implements OnInit, OnDestroy {
  private transferOutService = inject(TransferOutService);
  private toastr = inject(ToastrService);
  private route = inject(ActivatedRoute);

  // Modal States
  showCancelModal = false;
  showUpdateModal = false;
  selectedEwayBill: any = null;

  // Form Data
  cancelData = {
    cancelRmrk: '',
    cancelRsnCode: 2,
  };

  updateData = {
    fromPlace: '',
    fromState: '',
    reasonCode: '1',
    reasonRemark: '',
    vehicleNo: '',
    transMode: '1',
    transDocNo: '',
    transDocDate: '',
  };

  // Options
  cancelReasonOptions = [
    { value: 1, label: 'Duplicate' },
    { value: 2, label: 'Order Cancelled' },
    { value: 3, label: 'Data Entry Mistake' },
    { value: 4, label: 'Others' },
  ];

  updateReasonOptions = [
    { value: '1', label: 'Due to Break Down' },
    { value: '2', label: 'Due to Transshipment' },
    { value: '3', label: 'Others (Pls Specify)' },
  ];

  transModes = [
    { value: '1', label: 'Road' },
    { value: '2', label: 'Rail' },
    { value: '3', label: 'Air' },
    { value: '4', label: 'Ship' },
  ];

  openCancelModal(bill: any) {
    this.selectedEwayBill = bill;
    this.cancelData = {
      cancelRmrk: '',
      cancelRsnCode: 2,
    };
    this.showCancelModal = true;
  }

  openUpdateModal(bill: any) {
    this.selectedEwayBill = bill;
    this.updateData = {
      fromPlace: '',
      fromState: '',
      reasonCode: '1',
      reasonRemark: '',
      vehicleNo: '',
      transMode: '1',
      transDocNo: '',
      transDocDate: '',
    };
    this.showUpdateModal = true;
  }

  closeModals() {
    this.showCancelModal = false;
    this.showUpdateModal = false;
    this.selectedEwayBill = null;
  }

  submitCancel() {
    if (!this.selectedEwayBill) return;

    const payload = {
      ewbNo: this.selectedEwayBill.ewayBillNo,
      ...this.cancelData,
    };

    this.transferOutService.cancelEwayBill(payload).subscribe({
      next: () => {
        this.toastr.success('E-Way Bill cancelled successfully');
        this.closeModals();
        this.loadEwayBills();
      },
      error: (err: any) => {
        console.error(err);
        this.toastr.error('Failed to cancel E-Way Bill');
      },
    });
  }

  submitUpdate() {
    if (!this.selectedEwayBill) return;

    const payload = {
      ewbNo: this.selectedEwayBill.ewayBillNo,
      ...this.updateData,
    };

    this.transferOutService.updateEwayBillPartB(payload).subscribe({
      next: () => {
        this.toastr.success('E-Way Bill Part-B updated successfully');
        this.closeModals();
        this.loadEwayBills();
      },
      error: (err: any) => {
        console.error(err);
        this.toastr.error('Failed to update E-Way Bill Part-B');
      },
    });
  }

  activeTab: 'transfer_out' | 'eway_bill' = 'transfer_out';

  // Outbound sub-tabs by lifecycle status
  outboundStatusTab: 'drafts' | 'orders' | 'in_transit' | 'completed' = 'in_transit';

  transferList: any[] = [];
  transferLoading = false;
  currentPage = 1;
  pageSize = 10;
  pageCount = 1;
  searchTerm = '';

  ewayBillList: any[] = [];
  ewayLoading = false;
  ewayCurrentPage = 1;
  ewayPageSize = 10;
  ewayPageCount = 1;

  /**
   * Search keystrokes are debounced: firing a request per keystroke let a
   * slow, less-filtered response land after the latest one and overwrite the
   * list with rows that did not match what was typed.
   */
  private readonly search$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  /** Only the newest list request may write the table. */
  private loadSeq = 0;

  ngOnInit(): void {
    this.search$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.currentPage = 1;
        this.loadTransfers();
      });

    this.route.queryParams.subscribe(params => {
      if (params['tab'] === 'eway_bill') {
        this.activeTab = 'eway_bill';
      } else {
        this.activeTab = 'transfer_out';
      }
      this.loadTransfers();
      this.loadEwayBills();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadTransfers() {
    this.transferLoading = true;
    const seq = ++this.loadSeq;
    const statusByTab: Record<typeof this.outboundStatusTab, string> = {
      drafts: 'DRAFT',
      // Box-fulfilled orders: demand docs + their lifecycle (no stock movement
      // of their own; stock moves on the child shipment TOs under In Transit).
      orders: 'ORDERED,PARTIALLY_SHIPPED,SHIPPED,CLOSED',
      in_transit: 'IN_TRANSIT',
      completed: 'POSTED',
    };
    this.transferOutService
      .getTransferOuts(
        { page: this.currentPage, limit: this.pageSize },
        this.activeTab === 'transfer_out' ? this.searchTerm : '',
        {
          direction: 'outgoing',
          status: statusByTab[this.outboundStatusTab],
        }
      )
      .subscribe((res: any) => {
        if (seq !== this.loadSeq) return; // a newer request has superseded this one
        this.transferList = res.data;
        this.pageCount = res.pageCount;
        this.transferLoading = false;
      });
  }

  setOutboundTab(tab: 'drafts' | 'orders' | 'in_transit' | 'completed') {
    this.outboundStatusTab = tab;
    this.currentPage = 1;
    this.searchTerm = '';
    this.loadTransfers();
  }

  loadEwayBills() {
    this.ewayLoading = true;
    this.transferOutService
      .getEwayBills({ page: this.ewayCurrentPage, limit: this.ewayPageSize })
      .subscribe((res: any) => {
        // Assuming response structure is similar to transfers or provided sample
        this.ewayBillList = res.data || (Array.isArray(res) ? res : []);
        this.ewayPageCount = res.pageCount || 1;
        this.ewayLoading = false;
      });
  }

  goToPage(page: number) {
    if (this.activeTab === 'transfer_out') {
      if (page >= 1 && page <= this.pageCount) {
        this.currentPage = page;
        this.loadTransfers();
      }
    } else {
      if (page >= 1 && page <= this.ewayPageCount) {
        this.ewayCurrentPage = page;
        this.loadEwayBills();
      }
    }
  }

  onPageSizeChange(limit: number) {
    if (this.activeTab === 'transfer_out') {
      this.pageSize = limit;
    } else {
      this.ewayPageSize = limit;
    }
    this.pageSizeChange();
  }

  pageSizeChange() {
    if (this.activeTab === 'transfer_out') {
      this.currentPage = 1;
      this.loadTransfers();
    } else {
      this.ewayCurrentPage = 1;
      this.loadEwayBills();
    }
  }

  onSearch() {
    if (this.activeTab === 'transfer_out') {
      this.search$.next(this.searchTerm.trim());
    }
    // Implement eway search if API supports it later
  }

  downloadTransferOut(id: string) {
    this.transferOutService
      .downloadTransferOut(id)
      .subscribe((response: HttpResponse<Blob>) => {
        const blob = response.body as Blob;
        const filename = this.extractFilename(response, id);

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      });
  }

  private extractFilename(response: HttpResponse<Blob>, id: string): string {
    const disposition = response.headers.get('Content-Disposition') || '';
    let match = disposition.match(/filename\*=([^;]+)/i);
    if (match && match[1]) {
      return decodeURIComponent(match[1].split("''").pop() || '');
    }
    match = disposition.match(/filename="?([^";]+)"?/i);
    if (match && match[1]) {
      return match[1];
    }
    return `transfer-out-${id}.pdf`;
  }
}
